import React, { useState, useEffect, useCallback } from 'react';
import { Box, Grid, Modal, Fade, IconButton, useTheme, Typography, CircularProgress, Alert, Tooltip, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { supabase, getPublicUrl } from '../supabaseClient';
import { styled } from '@mui/system';

const StyledImage = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
  transition: 'transform 0.3s ease, opacity 0.3s ease',
  opacity: 0.96,
  '&:hover': {
    opacity: 1,
    transform: 'scale(1.03)'
  },
}));

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 23, 0.95)' : '#ffffff',
  borderRadius: 24,
  padding: { xs: theme.spacing(2), sm: theme.spacing(4), md: theme.spacing(6) },
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(138, 43, 226, 0.2)'
    : '0 8px 32px rgba(138, 43, 226, 0.1)',
  width: '100%',
  marginBottom: theme.spacing(4),
  position: 'relative',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 40px rgba(138, 43, 226, 0.3)'
      : '0 12px 40px rgba(138, 43, 226, 0.15)',
  },
  backdropFilter: 'blur(8px)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: theme.palette.mode === 'dark' ? `
      radial-gradient(circle at center, ${theme.palette.primary.main}15 0%, transparent 70%),
      repeating-linear-gradient(45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 1px, transparent 1px, transparent 10px),
      repeating-linear-gradient(-45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 1px, transparent 1px, transparent 10px)
    ` : `
      radial-gradient(circle at center, ${theme.palette.primary.main}10 0%, transparent 70%),
      repeating-linear-gradient(45deg, ${theme.palette.primary.main}05 0px, ${theme.palette.primary.main}05 1px, transparent 1px, transparent 10px),
      repeating-linear-gradient(-45deg, ${theme.palette.primary.main}05 0px, ${theme.palette.primary.main}05 1px, transparent 1px, transparent 10px)
    `,
    backgroundSize: '100% 100%, 20px 20px, 20px 20px',
    opacity: 0.5,
    pointerEvents: 'none',
    zIndex: 0,
    borderRadius: 'inherit'
  }
}));

const GalleryThumbnails = ({ onImageSelect, newImage }) => { // Add newImage prop
  const theme = useTheme();
  const [galleryImages, setGalleryImages] = useState([]);
  const [visibleCount, setVisibleCount] = useState(4);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchGalleryImages = useCallback(async () => {
    setLoadingGallery(true);
    setFetchError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Kullanıcı giriş yapmamış');
      }

      const { data, error, count } = await supabase
        .from('images')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(0, visibleCount - 1);

      setHasMore(count > visibleCount);

      if (error) {
        throw error;
      }

      const imagesWithOriginals = await Promise.all(data.map(async (image) => {
        const generatedUrl = image.url.startsWith('http')
          ? image.url
          : getPublicUrl('generated-images', image.url);

        const originalUrl = image.original_image_url
          ? (image.original_image_url.startsWith('http')
            ? image.original_image_url
            : getPublicUrl('uploaded-images', image.original_image_url))
          : null;

        return {
          id: image.id,
          generatedUrl,
          originalUrl,
          createdAt: image.created_at
        };
      }));

      setGalleryImages(imagesWithOriginals);

    } catch (error) {
      console.error('Galeri resimleri yüklenirken hata:', error);
      setFetchError('Resimler yüklenemedi. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setLoadingGallery(false);
    }
  }, [visibleCount]);

  useEffect(() => {
    fetchGalleryImages();
  }, [fetchGalleryImages]);

  // Effect to add newly generated image to the top of the list
  useEffect(() => {
    if (newImage && newImage.id && !galleryImages.some(img => img.id === newImage.id)) {
      const processNewImage = async () => {
        try {
          const generatedUrl = newImage.url.startsWith('http')
            ? newImage.url
            : getPublicUrl('generated-images', newImage.url);

          const originalUrl = newImage.original_image_url
            ? (newImage.original_image_url.startsWith('http')
              ? newImage.original_image_url
              : getPublicUrl('uploaded-images', newImage.original_image_url))
            : null;

          const formattedImage = {
            id: newImage.id,
            generatedUrl,
            originalUrl,
            createdAt: newImage.created_at
          };

          // Prepend the new image to the gallery state
          setGalleryImages(prevImages => [formattedImage, ...prevImages]);
          // Optionally, adjust visibleCount if needed, or let the "Load More" handle it
          // setVisibleCount(prev => prev + 1); // Or keep it the same

        } catch (error) {
          console.error("Error processing new gallery image:", error);
        }
      };
      processNewImage();
    }
  }, [newImage, galleryImages]); // Rerun when newImage or galleryImages changes

  const handleThumbnailClick = (image, type) => {
    setSelectedImage({ type, url: type === 'original' ? image.originalUrl : image.generatedUrl });
    onImageSelect(type === 'original' ? image.originalUrl : image.generatedUrl);
  };

  const handleZoomIn = (image, type) => {
    setZoomedImage({ type, url: type === 'original' ? image.originalUrl : image.generatedUrl });
    setIsZoomed(true);
  };

  const handleZoomOut = () => {
    setIsZoomed(false);
    setZoomedImage(null);
  };

  return (
    <StyledBox sx={{ position: 'relative', width: '100%', minHeight: '200px' }}>
      <Typography variant="subtitle1" sx={{
        mb: 2,
        fontWeight: 600,
        color: 'primary.main',
        fontSize: '1rem'
      }}>
        Son Resimler
      </Typography>

      {fetchError && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            borderRadius: '8px',
            '& .MuiAlert-message': {
              fontWeight: 500
            }
          }}
        >
          {fetchError}
        </Alert>
      )}

      {loadingGallery ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress size={24} sx={{ color: 'primary.main' }} />
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {galleryImages.slice(0, visibleCount).map((image) => (
              <Grid item xs={6} sm={4} md={3} key={image.id}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  marginBottom: 2
                }}>
                  {/* Generated Image */}
                  <Box
                    sx={{
                      position: 'relative',
                      paddingTop: '75%',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      borderRadius: '12px',
                      boxShadow: theme => `0 4px 20px ${theme.palette.primary.main}20`,
                      backgroundColor: theme.palette.mode === 'dark' ? '#1A1A25' : '#f5f5f5',
                      transition: 'all 0.3s ease-in-out',
                      border: theme => `1px solid ${theme.palette.primary.main}20`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme => `0 8px 24px ${theme.palette.primary.main}30`,
                      }
                    }}
                  >
                    <StyledImage
                      src={image.generatedUrl}
                      alt={`Oluşturulan resim ${image.id}`}
                        loading="lazy"
                        onClick={(e) => {
                          // Only handle click if not from a menu button
                          const isFromMenu = e.target.closest('.image-menu-button');
                          if (!isFromMenu) {
                            e.stopPropagation();
                            handleThumbnailClick(image, 'generated');
                          }
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          handleZoomIn(image, 'generated');
                        }}
                      />
                    <Box sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      backgroundColor: 'rgba(138, 43, 226, 0.9)',
                      color: '#fff',
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderRadius: '6px',
                      backdropFilter: 'blur(4px)'
                    }}>
                      YZ
                    </Box>
                  </Box>

                  {/* Original Image */}
                  {image.originalUrl && (
                    <Box
                      sx={{
                        position: 'relative',
                        paddingTop: '75%',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        borderRadius: '12px',
                        boxShadow: theme => `0 4px 20px ${theme.palette.primary.main}20`,
                        backgroundColor: theme.palette.mode === 'dark' ? '#1A1A25' : '#f5f5f5',
                        transition: 'all 0.3s ease-in-out',
                        border: theme => `1px solid ${theme.palette.primary.main}20`,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme => `0 8px 24px ${theme.palette.primary.main}30`,
                        }
                      }}
                    >
                      <Tooltip title="">
                        <StyledImage
                          src={image.originalUrl}
                          alt={`Orijinal resim ${image.id}`}
                          loading="lazy"
                          onClick={(e) => {
                            // Only handle click if not from a menu button
                            const isFromMenu = e.target.closest('.image-menu-button');
                            if (!isFromMenu) {
                              e.stopPropagation();
                              handleThumbnailClick(image, 'original');
                            }
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            handleZoomIn(image, 'original');
                          }}
                        />
                      </Tooltip>
                      <Box sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        backgroundColor: 'rgba(138, 43, 226, 0.9)',
                        color: '#fff',
                        padding: '4px 8px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        borderRadius: '6px',
                        backdropFilter: 'blur(4px)'
                      }}>
                        Girdi
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
            {galleryImages.length === 0 && (
              <Typography sx={{ 
                ml: 2, 
                color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                fontWeight: 500
              }}>
                Henüz resim oluşturulmamış
              </Typography>
            )}
          </Grid>

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                size="medium"
                onClick={() => setVisibleCount(prev => prev + 4)}
                sx={{
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  borderRadius: '8px',
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: theme => theme.palette.primary.main + '10',
                  }
                }}
              >
                Daha Fazla Göster
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Zoom Modal */}
      <Modal
        open={isZoomed}
        onClose={handleZoomOut}
        aria-labelledby="zoom-modal-title"
        aria-describedby="zoom-modal-description"
      >
        <Fade in={isZoomed} timeout={300}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            p: 3,
            borderRadius: '16px',
            boxShadow: theme => `0 12px 40px ${theme.palette.primary.main}30`,
            border: theme => `1px solid ${theme.palette.primary.main}20`,
          }}>
            {zoomedImage && (
              <>
                <Box sx={{ position: 'relative' }}>
                  <img 
                    src={zoomedImage.url} 
                    alt="Büyütülmüş Resim" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '70vh',
                      borderRadius: '8px'
                    }} 
                  />
                  <IconButton
                    onClick={handleZoomOut}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: '#fff',
                      backgroundColor: 'rgba(138, 43, 226, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(138, 43, 226, 0.9)',
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    display: 'block',
                    mt: 2,
                    textAlign: 'center',
                    fontWeight: 500
                  }}
                >
                  {zoomedImage.type === 'original' ? 'Orijinal Resim' : 'Oluşturulan Resim'}
                </Typography>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </StyledBox>
  );
};

export default GalleryThumbnails;
